/**
 * The one paginator. `<Pagination>` is the only place the console draws page controls —
 * the same way every table goes through `DataTable`. It is purely presentational; two
 * drivers feed it the same `PageControl` shape, so a page swaps one for the other without
 * touching the UI:
 *
 *   • `usePagination` + `cursorControl` — server cursor pagination. The API hands back an
 *     opaque `next_cursor` and nothing else, so this keeps a stack of the cursors it has
 *     walked, which is what turns a forward-only API into First / Back / Older.
 *   • `usePagedList` — client-side paging over a list already in hand (a live, bounded
 *     query like the job queue, where a server cursor would fight the auto-refresh).
 */
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "./ui";

/** What `<Pagination>` needs to render. Every driver below produces exactly this. */
export interface PageControl {
  page: number;
  atStart: boolean;
  hasNext: boolean;
  first: () => void;
  back: () => void;
  forward: () => void;
}

/** Optional page-size selector. When passed, `<Pagination>` shows a `per page` toggle —
 * one definition, so every list offers the same choice. */
export interface PageSize {
  value: number;
  options: readonly number[];
  onChange: (n: number) => void;
}

export function Pagination({
  control,
  label,
  size,
}: {
  control: PageControl;
  label?: ReactNode;
  size?: PageSize;
}) {
  const nav = !(control.atStart && !control.hasNext);
  if (!nav && !size) return null;
  return (
    <div className="flex items-center gap-2">
      {size && (
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-[10px] tracking-[0.16em] text-faint uppercase">per page</span>
          <span className="flex border border-hair">
            {size.options.map((n) => (
              <button
                key={n}
                aria-pressed={n === size.value}
                onClick={() => size.onChange(n)}
                className={`border-s border-hair px-2 py-1 font-mono text-[10.5px] tracking-[0.1em] transition-colors duration-200 ease-alt first:border-s-0 ${
                  n === size.value ? "bg-gold-surface font-bold text-on-gold" : "text-dim hover:text-gold"
                }`}
              >
                {n}
              </button>
            ))}
          </span>
        </span>
      )}
      {nav && (
        <>
          {label && (
            <span className="font-mono text-[10.5px] tracking-[0.14em] text-faint uppercase">{label}</span>
          )}
          <span className="tnum font-mono text-[10.5px] tracking-[0.16em] text-faint uppercase">
            page {control.page}
          </span>
          <Button variant="ghost" onClick={control.first} disabled={control.atStart}>
            ⏮ newest
          </Button>
          <Button variant="ghost" onClick={control.back} disabled={control.atStart}>
            ← newer
          </Button>
          <Button onClick={control.forward} disabled={!control.hasNext}>
            older →
          </Button>
        </>
      )}
    </div>
  );
}

/** A walked stack of server cursors. `cursor` feeds the query; `forward` takes the
 * response's `next_cursor` (so it can only be built after the query result is in hand —
 * see `cursorControl`). */
export interface CursorPager {
  cursor: string | null;
  page: number;
  atStart: boolean;
  first: () => void;
  back: () => void;
  forward: (nextCursor: string | null | undefined) => void;
}

export function usePagination(): CursorPager {
  const [stack, setStack] = useState<(string | null)[]>([null]);
  return {
    cursor: stack[stack.length - 1] ?? null,
    page: stack.length,
    atStart: stack.length === 1,
    first: () => setStack([null]),
    back: () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)),
    forward: (next) => {
      if (next != null) setStack((s) => [...s, next]);
    },
  };
}

/** Adapt a `CursorPager` + the current page's `next_cursor` into a `PageControl`. */
export function cursorControl(pager: CursorPager, next: string | null | undefined): PageControl {
  return {
    page: pager.page,
    atStart: pager.atStart,
    hasNext: next != null,
    first: pager.first,
    back: pager.back,
    forward: () => pager.forward(next),
  };
}

/**
 * Client-side paging over a list already fetched. `resetKey` sends it back to the first
 * page when it changes — pass the filter so switching filters starts at the top.
 */
export function usePagedList<T>(
  items: T[],
  size = 50,
  resetKey?: unknown,
): { items: T[]; total: number; control: PageControl } {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [resetKey]);

  const pageCount = Math.max(1, Math.ceil(items.length / size));
  const current = Math.min(page, pageCount - 1);
  return {
    items: items.slice(current * size, current * size + size),
    total: items.length,
    control: {
      page: current + 1,
      atStart: current === 0,
      hasNext: current < pageCount - 1,
      first: () => setPage(0),
      back: () => setPage((p) => Math.max(0, p - 1)),
      forward: () => setPage(() => Math.min(pageCount - 1, current + 1)),
    },
  };
}
